import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Plus, Check, X, RefreshCw, CircleDollarSign, Receipt } from 'lucide-react';
import { toast } from 'sonner';

/**
 * SuperAdmin "Ek Ücretler" panel.
 *
 * Lists all manually-defined extra charges (SMS package, custom dev, etc.)
 * across all businesses + lets SA create new charges, manually mark them
 * paid (havale/EFT/nakit) or cancel them.
 *
 * The business pays via the existing PayTR flow on their dashboard.
 *
 * Props:
 *   businesses: list from parent (for the business selector)
 *   presetBusinessId (optional): if set, the create-modal opens preselected
 *   onModalClose (optional): callback when preset modal is closed
 */
export const ExtraChargesPanel = ({ businesses = [], presetBusinessId, onModalClose }) => {
  const API_URL = process.env.REACT_APP_API_URL;
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total_pending: 0, total_paid: 0 });
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ business_id: '', title: '', description: '', amount: '' });
  const [submitting, setSubmitting] = useState(false);
  const [markPaidTarget, setMarkPaidTarget] = useState(null);
  const [mpForm, setMpForm] = useState({ method: 'havale', note: '' });

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API_URL}/api/superadmin/extra-charges${statusFilter ? `?status=${statusFilter}` : ''}`;
      const { data } = await axios.get(url, { withCredentials: true });
      setItems(data.items || []);
      setStats({ total_pending: data.total_pending || 0, total_paid: data.total_paid || 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Liste alınamadı');
    } finally {
      setLoading(false);
    }
  }, [API_URL, statusFilter]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Open modal when parent presets a business
  useEffect(() => {
    if (presetBusinessId) {
      setForm({ business_id: presetBusinessId, title: '', description: '', amount: '' });
      setCreateOpen(true);
    }
  }, [presetBusinessId]);

  const handleCreate = async () => {
    if (!form.business_id) return toast.error('İşletme seçin');
    if (!form.title || form.title.trim().length < 2) return toast.error('Başlık girin');
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) return toast.error('Geçerli tutar girin');
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/superadmin/extra-charges`, {
        business_id: form.business_id,
        title: form.title.trim(),
        description: form.description.trim() || null,
        amount: amt,
      }, { withCredentials: true });
      toast.success('Ek ücret oluşturuldu. İşletmeye e-posta gönderildi.');
      setCreateOpen(false);
      setForm({ business_id: '', title: '', description: '', amount: '' });
      onModalClose?.();
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Oluşturulamadı');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseCreate = () => {
    setCreateOpen(false);
    setForm({ business_id: '', title: '', description: '', amount: '' });
    onModalClose?.();
  };

  const handleMarkPaid = async () => {
    if (!markPaidTarget) return;
    try {
      await axios.post(`${API_URL}/api/superadmin/extra-charges/${markPaidTarget.id}/mark-paid`,
        { method: mpForm.method, note: mpForm.note || null },
        { withCredentials: true });
      toast.success('Ödendi olarak işaretlendi · müşteriye e-posta gönderildi');
      setMarkPaidTarget(null);
      setMpForm({ method: 'havale', note: '' });
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'İşaretlenemedi');
    }
  };

  const handleCancel = async (id, title) => {
    if (!window.confirm(`"${title}" ek ücretini iptal etmek istiyor musunuz?`)) return;
    try {
      await axios.post(`${API_URL}/api/superadmin/extra-charges/${id}/cancel`, {},
        { withCredentials: true });
      toast.success('İptal edildi');
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'İptal edilemedi');
    }
  };

  const fmt = (n) => (n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtDate = (iso) => { if (!iso) return '-'; try { return new Date(iso).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return iso; } };
  const statusBadge = (s) => ({
    pending: <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Bekliyor</Badge>,
    paid: <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Ödendi</Badge>,
    cancelled: <Badge variant="outline" className="bg-gray-50 text-gray-500 border-gray-200">İptal</Badge>,
  }[s] || <Badge variant="outline">{s}</Badge>);
  const methodLabel = (m) => ({ havale: 'Havale', eft: 'EFT', nakit: 'Nakit', card: 'Kart (PayTR)' }[m] || m || '-');

  const summary = (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-5">
          <p className="text-sm text-amber-700 mb-1">Bekleyen Ek Ücret</p>
          <p className="text-3xl font-bold text-amber-700" data-testid="extra-pending-total">{fmt(stats.total_pending)} TL</p>
        </CardContent>
      </Card>
      <Card className="border-emerald-200 bg-emerald-50">
        <CardContent className="p-5">
          <p className="text-sm text-emerald-700 mb-1">Toplam Tahsil Edilen</p>
          <p className="text-3xl font-bold text-emerald-700" data-testid="extra-paid-total">{fmt(stats.total_paid)} TL</p>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#1A1A2E]" data-testid="extra-charges-title">
            <Receipt className="inline w-7 h-7 mr-2 text-[#2563EB]" /> Ek Ücretler
          </h1>
          <p className="text-sm text-[#595959] mt-1">İşletmelere manuel olarak ücret tanımlayın (SMS paketi, özel hizmet, vb.)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={fetchItems} data-testid="extra-refresh"><RefreshCw className="w-4 h-4 mr-1" />Yenile</Button>
          <Button onClick={() => setCreateOpen(true)} className="bg-[#2563EB] hover:bg-[#1E4ED8] text-white gap-1" data-testid="extra-new-btn">
            <Plus className="w-4 h-4" /> Yeni Ücret Belirle
          </Button>
        </div>
      </div>

      {summary}

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-xs text-[#595959]">Durum:</span>
        {[
          { id: '', label: 'Tümü' },
          { id: 'pending', label: 'Bekliyor' },
          { id: 'paid', label: 'Ödendi' },
          { id: 'cancelled', label: 'İptal' },
        ].map(f => (
          <Button
            key={f.id || 'all'}
            size="sm"
            variant={statusFilter === f.id ? 'default' : 'outline'}
            onClick={() => setStatusFilter(f.id)}
            className={statusFilter === f.id ? 'bg-[#2563EB] hover:bg-[#1E4ED8] text-white' : ''}
            data-testid={`extra-filter-${f.id || 'all'}`}
          >{f.label}</Button>
        ))}
      </div>

      <Card className="border border-[#E5E5E5]">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>İşletme</TableHead>
                <TableHead>Başlık / Açıklama</TableHead>
                <TableHead>Tutar</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead>Tarih</TableHead>
                <TableHead>Yöntem</TableHead>
                <TableHead className="text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={7} className="text-center text-[#595959] py-8">Yükleniyor...</TableCell></TableRow>
              )}
              {!loading && items.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-[#595959] py-8">Kayıt bulunmuyor</TableCell></TableRow>
              )}
              {!loading && items.map(it => (
                <TableRow key={it.id} data-testid={`extra-row-${it.id}`}>
                  <TableCell className="font-medium">{it.business_name || '-'}</TableCell>
                  <TableCell>
                    <div className="font-medium text-sm">{it.title}</div>
                    {it.description && <div className="text-xs text-[#595959] mt-0.5">{it.description}</div>}
                  </TableCell>
                  <TableCell className="font-mono font-semibold">₺{fmt(it.amount)}</TableCell>
                  <TableCell>{statusBadge(it.payment_status)}</TableCell>
                  <TableCell className="text-xs text-[#595959]">
                    <div>{fmtDate(it.created_at)}</div>
                    {it.paid_at && <div className="text-emerald-700 mt-0.5">Ödendi: {fmtDate(it.paid_at)}</div>}
                  </TableCell>
                  <TableCell className="text-xs">{methodLabel(it.method)}</TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      {it.payment_status === 'pending' && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => setMarkPaidTarget(it)} title="Ödendi İşaretle" data-testid={`extra-markpaid-${it.id}`}>
                            <Check className="w-4 h-4 text-emerald-600" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleCancel(it.id, it.title)} title="İptal" data-testid={`extra-cancel-${it.id}`}>
                            <X className="w-4 h-4 text-red-600" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ===== Create Modal ===== */}
      <Dialog open={createOpen} onOpenChange={(o) => !o && handleCloseCreate()}>
        <DialogContent data-testid="extra-create-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CircleDollarSign className="w-5 h-5 text-[#2563EB]" /> Yeni Ek Ücret</DialogTitle>
            <DialogDescription>İşletmeye serbest tutarlı manuel borç tanımlayın. Müşterinin paneline anında yansır ve e-posta gider.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>İşletme</Label>
              <Select value={form.business_id} onValueChange={(v) => setForm({ ...form, business_id: v })}>
                <SelectTrigger data-testid="extra-biz-select"><SelectValue placeholder="İşletme seçin..." /></SelectTrigger>
                <SelectContent>
                  {(businesses || []).map(b => (
                    <SelectItem key={b.id || b.business_id} value={b.id || b.business_id} data-testid={`extra-biz-opt-${b.id || b.business_id}`}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Başlık *</Label>
              <Input
                placeholder="Örn: SMS Paketi 1000 Adet"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                maxLength={200}
                data-testid="extra-title-input"
              />
            </div>
            <div>
              <Label>Açıklama (opsiyonel)</Label>
              <Textarea
                placeholder="Müşteriye iletilecek ek bilgi..."
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                maxLength={2000}
                data-testid="extra-desc-input"
              />
            </div>
            <div>
              <Label>Tutar (TL) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                placeholder="500.00"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                data-testid="extra-amount-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={handleCloseCreate} disabled={submitting}>Vazgeç</Button>
              <Button onClick={handleCreate} className="bg-[#2563EB] hover:bg-[#1E4ED8] text-white" disabled={submitting} data-testid="extra-create-submit">
                {submitting ? 'Oluşturuluyor...' : 'Oluştur'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== Mark-Paid Modal ===== */}
      <Dialog open={!!markPaidTarget} onOpenChange={(o) => !o && setMarkPaidTarget(null)}>
        <DialogContent data-testid="extra-markpaid-modal">
          <DialogHeader>
            <DialogTitle>Ödendi Olarak İşaretle</DialogTitle>
            <DialogDescription>
              {markPaidTarget && (
                <>
                  <strong>{markPaidTarget.business_name}</strong> · {markPaidTarget.title} · <strong>₺{fmt(markPaidTarget.amount)}</strong>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Ödeme Yöntemi</Label>
              <Select value={mpForm.method} onValueChange={(v) => setMpForm({ ...mpForm, method: v })}>
                <SelectTrigger data-testid="extra-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="havale">Havale</SelectItem>
                  <SelectItem value="eft">EFT</SelectItem>
                  <SelectItem value="nakit">Nakit</SelectItem>
                  <SelectItem value="other">Diğer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Not (opsiyonel)</Label>
              <Input
                placeholder="TX#12345, dekont no, vb."
                value={mpForm.note}
                onChange={(e) => setMpForm({ ...mpForm, note: e.target.value })}
                data-testid="extra-note-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setMarkPaidTarget(null)}>Vazgeç</Button>
              <Button onClick={handleMarkPaid} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" data-testid="extra-markpaid-submit">
                <Check className="w-4 h-4" /> Ödendi İşaretle
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
